import { useAuthStore } from '@/lib/store/authStore';

/**
 * Hook for checking current user's app access.
 * Returns helpers to check if user has access to specific apps.
 *
 * Access logic:
 * - Direct mode (local dev): all access granted
 * - Admin/Owner: all access granted
 * - appAccess not yet loaded (null): grant access (fail-open to prevent flash)
 * - Member: check appAccess array
 */
export function useAppAccess() {
  const authMode = useAuthStore((s) => s.authMode);
  const appAccess = useAuthStore((s) => s.appAccess);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const appAccessLoaded = useAuthStore((s) => s.appAccessLoaded);
  const installedApps = useAuthStore((s) => s.installedApps);

  /** Check member-level access (admin bypass, fail-open during load) */
  function hasAccess(appKey: string): boolean {
    // Direct mode — no CP, no restrictions
    if (authMode === 'direct') return true;
    // Admin always has full access
    if (isAdmin) return true;
    // Not yet loaded — fail-open to prevent sidebar flash
    if (appAccess === null) return true;
    // Check member's access list
    return appAccess.includes(appKey);
  }

  /** Check tenant-level install (no admin bypass — uninstalled is hidden for everyone) */
  function isInstalled(appRegistryKey: string): boolean {
    // Direct mode — no CP, no restrictions
    if (authMode === 'direct') return true;
    // Not yet loaded — fail-open to prevent flash
    if (!appAccessLoaded) return true;
    // Check tenant's installed apps list
    return installedApps.includes(appRegistryKey);
  }

  return { appAccess, isAdmin, appAccessLoaded, installedApps, hasAccess, isInstalled };
}
